import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import type { ArquivosEtapa2, ArquivosFilhoEtapa2 } from './candidatoFormExternoEtapa2Page.definitions';

/** Nome de cada documento nas mensagens de erro pro candidato. */
const NOMES_DOCUMENTOS: Record<string, string> = {
  identidadeFrente: 'Documento de identificação (frente)',
  identidadeVerso: 'Documento de identificação (verso)',
  comprovanteEndereco: 'Comprovante de endereço',
  fotoRecente: 'Foto recente',
  certidaoCasamento: 'Certidão de casamento/união estável',
  rgConjuge: 'RG do cônjuge',
  rgMae: 'RG da mãe',
  rgPai: 'RG do pai',
  laudoPcd: 'Laudo médico PCD',
  certidaoNascimento: 'Certidão de nascimento',
  rgIdentidade: 'RG/Identidade',
};

/** Lê um File como base64 puro (sem o prefixo `data:...;base64,`) — sobe pelo Cloud Function
 * (Admin SDK), porque o candidato não está autenticado pra usar o Storage direto. */
async function arquivoParaBase64(arquivo: File): Promise<{ base64: string; nomeArquivo: string; tipo: string }> {
  const buffer = await arquivo.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binario = '';
  for (let i = 0; i < bytes.byteLength; i++) binario += String.fromCharCode(bytes[i]);
  return { base64: btoa(binario), nomeArquivo: arquivo.name, tipo: arquivo.type };
}

interface Envio {
  documento: string;
  indiceFilho?: number;
  arquivo: File;
}

/**
 * Sobe os anexos UM POR VEZ (uploadDocumentoCandidatoEtapa2) e devolve só as URLs pro envio
 * final. Antes iam todos juntos com as respostas numa chamada só, com limite de ~10 MB no
 * total — dois ou três documentos grandes estouravam e o candidato via só "internal".
 */
export async function enviarDocumentosEtapa2(
  token: string,
  arquivos: ArquivosEtapa2,
  arquivosFilhos: ArquivosFilhoEtapa2[],
  onProgresso: (enviados: number, total: number) => void
): Promise<{ documentosUrls: Record<string, string>; documentosFilhosUrls: Array<Record<string, string>> }> {
  const envios: Envio[] = [];
  for (const [documento, arquivo] of Object.entries(arquivos)) {
    if (arquivo) envios.push({ documento, arquivo });
  }
  arquivosFilhos.forEach((docs, indiceFilho) => {
    for (const [documento, arquivo] of Object.entries(docs)) {
      if (arquivo) envios.push({ documento, indiceFilho, arquivo });
    }
  });

  const upload = httpsCallable<
    { token: string; documento: string; indiceFilho?: number; arquivo: Awaited<ReturnType<typeof arquivoParaBase64>> },
    { url: string }
  >(getFunctions(app, 'us-central1'), 'uploadDocumentoCandidatoEtapa2');

  const documentosUrls: Record<string, string> = {};
  const documentosFilhosUrls: Array<Record<string, string>> = arquivosFilhos.map(() => ({}));

  for (let i = 0; i < envios.length; i++) {
    const { documento, indiceFilho, arquivo } = envios[i];
    onProgresso(i, envios.length);
    try {
      const { data } = await upload({ token, documento, indiceFilho, arquivo: await arquivoParaBase64(arquivo) });
      if (indiceFilho === undefined) documentosUrls[documento] = data.url;
      else documentosFilhosUrls[indiceFilho][documento] = data.url;
    } catch (erro: any) {
      const nome = NOMES_DOCUMENTOS[documento] ?? documento;
      throw new Error(`Não foi possível enviar "${nome}" (${arquivo.name}): ${erro?.message || 'erro desconhecido'}`);
    }
  }
  return { documentosUrls, documentosFilhosUrls };
}
